import { Control, Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import { Typography } from '@mui/material';
import { readableDateTimeFromDate } from '@utils/date-utils';
import { DateTimeValidationError } from '@mui/x-date-pickers/models';
import { useMemo, useState } from 'react';

export interface FormInputProps extends Omit<ControllerProps, 'name' | 'control' | 'render'> {
    editable?: boolean;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control?: Control<any, any>;
    dateTimePickerProps?: DateTimePickerProps<Date>;
    label?: string;
}

export default function FormInputDateTime({
    editable = true,
    name,
    control,
    dateTimePickerProps,
    label,
    ...rest
}: FormInputProps) {
    const { trigger } = useFormContext();
    const [error, setError] = useState<DateTimeValidationError | null>(null);

    const errorMessage = useMemo(() => {
        switch (error) {
            case 'maxDate':
            case 'maxTime':
                return `Please select a valid date before the other date`;

            case 'minDate':
            case 'minTime':
                return `Please select a valid date in the future`;

            case 'invalidDate':
                return 'Your date is not valid';

            default: {
                return '';
            }
        }
    }, [error]);

    return (
        <Controller
            {...rest}
            name={name}
            control={control}
            render={({ field: { onChange, value, ...restField }, fieldState: { error } }) =>
                editable ? (
                    <DateTimePicker
                        {...dateTimePickerProps}
                        {...restField}
                        onError={(newError) => setError(newError)}
                        onChange={(e) => {
                            onChange(e);
                            // triggering Yup validation on change
                            trigger?.(name);
                        }}
                        slotProps={{
                            textField: { helperText: errorMessage || error?.message },
                        }}
                        value={typeof value === 'string' ? new Date(value) : value}
                        label={label}
                    />
                ) : (
                    <Typography variant="body1" className="value">
                        {readableDateTimeFromDate(value)}
                    </Typography>
                )
            }
        />
    );
}
